import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Clock, Sparkles } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF, IMAGES } from "@/data/site";
import { scrollTo } from "./Reveal";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ children, i, className = "" }) => (
  <span className={`block overflow-hidden pb-1 -mb-1 ${className}`}>
    <motion.span custom={i} variants={line} className="block will-change-transform">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-[#FFF0F5] via-[#FDF3F7] to-white pt-28 pb-16 lg:pt-32"
    >
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#F8BBD0]/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-[#C2185B]/8 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
        <motion.div initial="hidden" animate="show" className="lg:col-span-7">
          <MaskedLine i={0}>
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 border border-[#C2185B]/25 bg-white/70 backdrop-blur px-4 py-2 rounded-full text-xs font-medium tracking-[0.22em] uppercase text-[#C2185B]"
            >
              <Sparkles size={13} />
              Realce sua beleza ♡ Revele sua essência
            </span>
          </MaskedLine>

          <h1
            data-testid="hero-title"
            className="mt-8 font-serif-display font-semibold text-stone-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight"
          >
            <MaskedLine i={1}>Cuidado, Elegância</MaskedLine>
            <MaskedLine i={2}>
              e <em className="text-[#C2185B] font-medium">Perfeição</em>
            </MaskedLine>
            <MaskedLine i={3}>Para Suas Unhas</MaskedLine>
          </h1>

          <MaskedLine i={4}>
            <p data-testid="hero-slogan" className="mt-6 max-w-xl text-base sm:text-lg text-stone-600 leading-relaxed">
              Realce sua beleza e revele sua essência em um ambiente feito
              exclusivamente para o seu bem-estar.
            </p>
          </MaskedLine>

          <MaskedLine i={5}>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                data-testid="hero-cta-call"
                href={PHONE_HREF}
                className="group inline-flex items-center gap-3 bg-[#C2185B] hover:bg-[#A2134B] text-white font-semibold px-8 py-4 rounded-full text-base transition-all duration-300 hover:shadow-2xl hover:shadow-[#C2185B]/35 hover:-translate-y-1"
              >
                <span className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                  <Phone size={15} strokeWidth={2.5} />
                </span>
                Ligue para Agendar: {PHONE_DISPLAY}
              </a>
              <button
                data-testid="hero-cta-services"
                onClick={() => scrollTo("#servicos")}
                className="inline-flex items-center gap-2 text-stone-800 font-medium px-6 py-4 rounded-full border border-stone-300 hover:border-[#C2185B] hover:text-[#C2185B] transition-all duration-300 bg-white/60"
              >
                Explorar Serviços
                <ArrowDown size={15} />
              </button>
            </div>
          </MaskedLine>

          <MaskedLine i={6}>
            <div className="mt-10 inline-flex items-center gap-3 text-sm text-stone-600 bg-white/70 backdrop-blur border border-rose-100 rounded-2xl px-5 py-3.5 shadow-sm">
              <Clock size={16} className="text-[#C2185B]" />
              <span>
                <strong className="text-stone-900">Terça a Sábado</strong> · 09:00–13:00 e
                15:00–18:00
              </span>
            </div>
          </MaskedLine>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-t-[10rem] rounded-b-3xl overflow-hidden shadow-2xl shadow-[#C2185B]/20 border-[6px] border-white aspect-[3/4] max-w-md mx-auto">
              <motion.img
                data-testid="hero-image"
                src={IMAGES.lounge}
                alt="Interior do Espaço da Beleza"
                style={{ y: imgY, scale: imgScale }}
                className="w-full h-full object-cover will-change-transform"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#C2185B]/25 via-transparent to-transparent" />
            </div>

            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl shadow-[#C2185B]/15 border border-rose-100 px-5 py-4 flex items-center gap-3">
              <div className="flex -space-x-1 text-[#D4AF37] text-sm">★★★★★</div>
              <div className="text-xs leading-tight">
                <p className="font-semibold text-stone-900">5.0 · Clientes Felizes</p>
                <p className="text-stone-500">Atendimento VIP</p>
              </div>
            </div>

            <div className="absolute -top-8 -right-2 sm:-right-6 w-28 h-28 hidden sm:block">
              <svg viewBox="0 0 100 100" className="spin-slow w-full h-full">
                <defs>
                  <path id="circ" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                </defs>
                <text fill="#C2185B" fontSize="10.5" letterSpacing="2.5">
                  <textPath href="#circ">ESPAÇO DA BELEZA • ESTÉTICA • UNHAS •</textPath>
                </text>
                <text x="50" y="56" textAnchor="middle" fontSize="18" fill="#C2185B" fontFamily="Cormorant Garamond, serif" fontStyle="italic">
                  EB
                </text>
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
